import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Flame, Sparkles, ArrowUpRight } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Rising this week" rail — films with the biggest week-over-week jump in
 * qualified streams. Different question than "Top of the month": this asks
 * "what's catching fire right now?" and protects mid-tier creators from being
 * permanently drowned out by top-of-catalog films.
 *
 * Silent on empty data. Renders under TopEarnersRail on /browse.
 */
export default function RisingRail() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/public/rising-films?limit=3`)
      .then((r) => setData(r.data))
      .catch((err) => {
        // Hide silently but log once so a 5xx doesn't disappear from monitoring.
        console.warn("[RisingRail] hidden due to fetch error:", err?.response?.status || err?.message);
        setData({ films: [] });
      });
  }, []);

  if (!data || !data.films || data.films.length === 0) return null;

  return (
    <section className="mb-12" data-testid="rising-rail">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center relative">
            <Flame className="h-5 w-5 text-red-400" />
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          </div>
          <div>
            <p className="text-eyebrow text-red-300/80">Momentum · last 7 days</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold">
              <span className="text-red-400">Rising</span> this week
            </h2>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="rising-grid">
        {data.films.map((f) => (
          <Link
            key={f.id}
            to={`/movie/${f.id}`}
            className="card-surface p-4 flex items-center gap-4 hover:border-red-500/40 transition group relative overflow-hidden"
            data-testid={`rising-film-${f.rank}`}
          >
            {/* subtle heat glow behind the card */}
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-red-500/10 blur-2xl rounded-full pointer-events-none" />

            <div className="w-16 h-24 shrink-0 rounded-md overflow-hidden bg-gradient-to-br from-[#1F1F1F] to-black border border-white/5 relative z-10">
              {f.poster_url ? (
                <img
                  src={f.poster_url}
                  alt={f.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <Flame className="h-6 w-6" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 relative z-10">
              <p className="font-display font-bold leading-tight line-clamp-2 group-hover:text-red-400 transition">
                {f.title}
              </p>
              {f.channel_name && (
                <p className="text-xs text-white/40 mt-1 truncate">{f.channel_name}</p>
              )}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {f.is_new ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/15 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider"
                    data-testid={`rising-new-${f.rank}`}
                  >
                    <Sparkles className="h-3 w-3" /> New
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-red-400 font-display font-black text-lg tabular-nums"
                    data-testid={`rising-delta-${f.rank}`}
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    +{f.delta_pct}%
                  </span>
                )}
                <span className="text-[11px] text-white/40 tabular-nums">
                  {f.streams_this_week} streams this week
                  {f.streams_prev_week > 0 ? ` · was ${f.streams_prev_week}` : ""}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
